// Variant selection store, shared between the PDP body and the sticky buy bars
// so they agree about which variant (e.g. 10mL) is chosen.
//
// Same pattern as cart-store (useSyncExternalStore + listener set), scoped per
// product: it resets when the active product changes so a selection never
// leaks from one PDP to the next.
//
// A PlazaStack variant is a flat list (id, title, price, stock, pre-order),
// not Wix option/choice shapes. Products with ONE variant select it
// automatically; products with several make the shopper choose.

'use client';

import { useSyncExternalStore, useEffect } from 'react';

export interface PdpVariant {
  id:         string;
  title:      string;
  priceCents: number;
  inStock:    boolean;
  /** In stock or open for pre-order. */
  canBuy:     boolean;
  available:  number;
  /** In-stock units plus open pre-order capacity, capped at 10. */
  maxQty:     number;
  preorder:   { shipsBy: string | null; remaining: number | null } | null;
}

interface State {
  productId: string | null;
  variantId: string | null;
}

let state: State = { productId: null, variantId: null };
const listeners = new Set<() => void>();
const SERVER_STATE: State = { productId: null, variantId: null };

function emit() { for (const fn of listeners) fn(); }

/** Make `productId` the active product; clears the selection when it changes. */
export function activateProduct(productId: string): void {
  if (state.productId === productId) return;
  state = { productId, variantId: null };
  emit();
}

export function selectVariant(variantId: string): void {
  state = { ...state, variantId };
  emit();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Pin this PDP's product as active. Call once per PDP render. */
export function useActivateProduct(productId: string): void {
  useEffect(() => {
    activateProduct(productId);
  }, [productId]);
}

export interface ResolvedVariant {
  hasVariants:   boolean;
  /** Single-variant products are always complete; multi-variant need a pick. */
  isComplete:    boolean;
  variant:       PdpVariant | null;
  variantId:     string | null;
  /** Price for the chosen variant, cents (undefined until chosen on multi-variant products). */
  priceCents:    number | undefined;
  canBuy:        boolean;
  inStock:       boolean;
  isPreorder:    boolean;
  shipsBy:       string | null;
}

export function useSelectedVariant(variants: PdpVariant[] | undefined | null): ResolvedVariant {
  const snap = useSyncExternalStore(subscribe, () => state, () => SERVER_STATE);
  const list = variants ?? [];
  const hasVariants = list.length > 1;

  const picked = hasVariants
    ? list.find((v) => v.id === snap.variantId) ?? null
    : list[0] ?? null;

  const stocked = !!picked?.inStock;
  const pre = !!picked && !picked.inStock && !!picked.preorder;
  return {
    hasVariants,
    isComplete:  !!picked,
    variant:     picked,
    variantId:   picked?.id ?? null,
    priceCents:  picked?.priceCents,
    canBuy:      !!picked?.canBuy,
    inStock:     stocked,
    isPreorder:  pre,
    shipsBy:     pre ? picked!.preorder!.shipsBy : null,
  };
}
